import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpUrlEncodingCodec,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs';
import {
  EliotRefreshTokenResponse,
  EliotTokenResponse,
} from 'src/app/models/eliotToken.model';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthService {
  private _accessToken: string;
  private _languageId: string = sessionStorage.getItem(environment.storage.eliotUser) ? JSON.parse(sessionStorage.getItem(environment.storage.eliotUser)).language : 'en';

  constructor(
    private httpClient: HttpClient,
    private oauthService: OAuthService
  ) {}

  //AUTH APIs

  set accessToken(token: string | undefined) {
    if (token) {
      sessionStorage.setItem(environment.storage.accessToken, token);
      this._accessToken = token;
    }
  }

  get accessToken(): string | undefined {
    let data: string;
    if (this._accessToken) return this._accessToken;
    else data = sessionStorage.getItem(environment.storage.accessToken);
    if (data != null) return data;
    else return undefined;
  }

  set idToken(token: string | undefined) {
    if (token) sessionStorage.setItem(environment.storage.idToken, token);
  }

  get idToken(): string | undefined {
    const data = sessionStorage.getItem(environment.storage.idToken);
    if (data != null) return data;
    else return undefined;
  }

  set refreshToken(token: string | undefined) {
    if (token) sessionStorage.setItem(environment.storage.refreshToken, token);
  }

  get refreshToken(): string | undefined {
    const data = sessionStorage.getItem(environment.storage.refreshToken);
    if (data != null) return data;
    else return undefined;
  }

  set tokenExpiresIn(ms: number | undefined) {
    if (ms)
      sessionStorage.setItem(environment.storage.tokenExpiresIn, String(ms));
  }

  get tokenExpiresIn(): number | undefined {
    const data = Number(
      sessionStorage.getItem(environment.storage.tokenExpiresIn)
    );
    if (data != null) return data;
    else return undefined;
  }

  public get languageId(): string {
    return this._languageId;
  }
  public set languageId(value: string) {
    this._languageId = value;
  }

  isAuthenticated(): Boolean {
    let res = false;
    if (this.accessToken != undefined) res = true;
    return res;
  }

  logout() {
    sessionStorage.removeItem(environment.storage.accessToken);
    sessionStorage.removeItem(environment.storage.refreshToken);
    sessionStorage.removeItem(environment.storage.idToken);
    sessionStorage.removeItem(environment.storage.tokenExpiresIn);
    sessionStorage.removeItem(environment.storage.eliotUser);
    sessionStorage.removeItem(environment.storage.upsUser);
    sessionStorage.removeItem(environment.storage.searchHistory);
    sessionStorage.removeItem(environment.storage.upsBackofficeLanguages);
    sessionStorage.removeItem(environment.storage.sasToken);
    sessionStorage.removeItem(environment.storage.articleCodes);
    sessionStorage.removeItem(environment.storage.selectedArticleCode);

    window.location.href =
      environment.logOutUrl +
      '&post_logout_redirect_uri=' +
      environment.logoutRedirectUri;
  }

  getAuthToken(code: string): Observable<EliotTokenResponse> {
    const tokenAuthConfig = this.getTokenConfiguration();
    sessionStorage.setItem(
      'UPService-token-endpoint',
      tokenAuthConfig.tokenEndpoint
    );
    return this.getAccessToken(
      code,
      tokenAuthConfig.tokenEndpoint,
      tokenAuthConfig
    );
  }

  getTokenConfiguration() {
    return environment.tokenAuth;
  }

  public getAccessToken(
    code: string,
    addressOAuth: string,
    confObj: any
  ): Observable<EliotTokenResponse> {
    const encoder: HttpUrlEncodingCodec = new HttpUrlEncodingCodec();
    const httpParams: HttpParams = new HttpParams()
      .append(encoder.encodeKey('code'), encoder.encodeValue(code))
      .append(
        encoder.encodeKey('client_id'),
        encoder.encodeValue(confObj.clientId)
      )
      .append(
        encoder.encodeKey('grant_type'),
        encoder.encodeValue(confObj.grantType)
      )
      .append(
        encoder.encodeKey('code_verifier'),
        encoder.encodeValue(sessionStorage.getItem('PKCE_verifier'))
      );
    return this.httpClient.post<EliotTokenResponse>(addressOAuth, httpParams);
  }

  configureLogin() {
    const codeAuthConfig = this.getCodeConfiguration();
    this.oauthService.configure(codeAuthConfig);
    this.oauthService.setStorage(sessionStorage);
    this.oauthService.initCodeFlow();
  }

  getCodeConfiguration() {
    return environment.codeAuth;
  }

  tryRefreshToken() {
    const config = this.getTokenConfiguration();
    const encoder: HttpUrlEncodingCodec = new HttpUrlEncodingCodec();
    const httpParams: HttpParams = new HttpParams()
      .append(
        encoder.encodeKey('grant_type'),
        encoder.encodeValue('refresh_token')
      )
      .append(
        encoder.encodeKey('refresh_token'),
        encoder.encodeValue(this.refreshToken)
      )
      .append(
        encoder.encodeKey('client_id'),
        encoder.encodeValue(config.clientId)
      )
      .append(
        encoder.encodeKey('code_verifier'),
        encoder.encodeValue(sessionStorage.getItem('PKCE_verifier'))
      );
    this.httpClient
      .post<EliotRefreshTokenResponse>(config.tokenEndpoint, httpParams)
      .subscribe((res: EliotRefreshTokenResponse) => {
        if (
          res.access_token != null &&
          res.refresh_token != null &&
          res.expires_in != null
        ) {
          this.accessToken = res.access_token;
          this.refreshToken = res.refresh_token;
          this.tokenExpiresIn = res.expires_in;
        } else {
          this.logout();
        }
      });
  }

  setTimeoutRefreshToken() {
    setInterval(() => {
      this.tryRefreshToken();
    }, this.tokenExpiresIn * 100);
  }

  get eliotHttpHeaders(): HttpHeaders {
    if (!this.isProduction())
      return new HttpHeaders({
        Authorization: `Bearer ${this.accessToken}`,
        'Ocp-Apim-Subscription-Key': environment.eliot_ocp_key,
        'x-Backend-Endpoint': 'eliot',
      });
    if (this.isProduction())
      return new HttpHeaders({
        Authorization: `Bearer ${this.accessToken}`,
        'x-Backend-Endpoint': 'eliot',
      });
  }

  get upsHttpHeaders(): HttpHeaders {
    return new HttpHeaders({
      'x-Backend-Endpoint': 'ups',
      'x-LanguageId': this.languageId,
      Authorization: `Bearer ${this.accessToken}`,
    });
    //return new HttpHeaders({'Authorization': `Bearer ${this.accessToken}`, 'x-Backend-Endpoint': 'ups'});
  }

  isProduction(): boolean {
    return environment.production;
  }
}
